import { useEffect, useRef, useState } from 'react';
import type { AppUser } from '../../../types';
import { followUser, subscribeToFollowStatus, unfollowUser } from '../../../backend/mediaService';

interface FollowButtonProps {
  targetUid: string;
  targetName: string;
  currentUser?: AppUser | null;
  onOpenAuth?: () => void;
  compact?: boolean;
}

export function FollowButton({ targetUid, targetName, currentUser, onOpenAuth, compact = false }: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!currentUser?.uid || !targetUid) {
      setIsFollowing(false);
      return;
    }
    return subscribeToFollowStatus(currentUser.uid, targetUid, following => {
      if (!busyRef.current) setIsFollowing(following);
    });
  }, [currentUser?.uid, targetUid]);

  const toggleFollow = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!targetUid || isBusy || currentUser.uid === targetUid) return;

    const previous = isFollowing;
    busyRef.current = true;
    setIsFollowing(!previous);
    setIsBusy(true);
    try {
      if (previous) await unfollowUser(targetUid, currentUser);
      else await followUser(targetUid, currentUser, targetName);
    } catch (error) {
      console.error('Could not update follow state:', error);
      setIsFollowing(previous);
    } finally {
      busyRef.current = false;
      setIsBusy(false);
    }
  };

  if (currentUser?.uid === targetUid) return null;
  return (
    <button
      type="button"
      onClick={event => {
        event.stopPropagation();
        void toggleFollow();
      }}
      disabled={isBusy}
      className={`${compact ? 'rounded-lg px-2 py-1' : 'mt-2 w-full rounded-xl px-3 py-1.5'} text-[9px] font-black uppercase tracking-widest shadow-sm transition-colors active:scale-95 disabled:opacity-60 ${
        isFollowing ? 'bg-slate-100 text-slate-600' : 'bg-slate-900 text-white hover:bg-blue-900'
      }`}
    >
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  );
}
